import api from './api/client';

const API_BASE = api.defaults.baseURL;

export const apiFetch = async (path, opts = {}) => {
    const { method = 'GET', body, params, headers, ...rest } = opts;

    try {
        const res = await api.request({
            url: path,
            method,
            data: body,
            params,
            headers,
            ...rest
        });
        return res.data;
    } catch (err) {
        const message = err.response?.data?.message || err.message || 'Request failed';
        throw new Error(message);
    }
};

export const fetchRestocks = async (params, sort) => {
    const queryParams = {};

    if (params?.current) queryParams.page = params.current;
    if (params?.pageSize) queryParams.limit = params.pageSize;
    if (params?.supplier) queryParams.supplier = params.supplier;
    if (params?.paymentStatus) queryParams.paymentStatus = params.paymentStatus;
    if (params?.reversedStatus) queryParams.reversedStatus = params.reversedStatus;
    if (params?.startDate && params?.endDate) {
        queryParams.startDate = params.startDate;
        queryParams.endDate = params.endDate;
    }

    if (sort) {
        const sortField = Object.keys(sort)[0];
        const sortOrder = sort[sortField];
        if (sortField && sortOrder) {
            queryParams.sort = `${sortField}:${sortOrder === 'ascend' ? 'ASC' : 'DESC'}`;
        }
    }

    try {
        const res = await api.get('/api/inventory', { params: queryParams });
        return {
            data: res.data.data,
            total: res.data.total,
            success: true
        };
    } catch (err) {
        console.error('Error fetching restocks:', err);
        return {
            data: [],
            total: 0,
            success: false
        };
    }
};

export const fetchInventorySummary = async (params, sort) => {
    const queryParams = {};

    if (params?.current) queryParams.page = params.current;
    if (params?.pageSize) queryParams.limit = params.pageSize;
    if (params?.search) queryParams.search = params.search; // 👈 was params.name — dead once you drop the inbuilt search form
    if (params?.type) queryParams.type = params.type;
    if (params?.category) queryParams.category = params.category;

    if (sort) {
        const sortField = Object.keys(sort)[0];
        const sortOrder = sort[sortField];
        if (sortField && sortOrder) {
            queryParams.sort = `${sortField}:${sortOrder === 'ascend' ? 'ASC' : 'DESC'}`;
        }
    }

    try {
        const res = await api.get('/api/items/inventory-summary', { params: queryParams });
        return {
            data: res.data.data,
            total: res.data.total,
            success: true
        };
    } catch (err) {
        console.error('Error fetching inventory summary:', err);
        return {
            data: [],
            total: 0,
            success: false
        };
    }
};

export const fetchInventorySummaryStats = (params) => api.get('/api/items/inventory-summary/stats', { params }).then((res) => res.data);

export const fetchItemFilterOptions = () => api.get('/api/items/filter-options').then((res) => res.data);

// export const fetchInventorySummary = async (params, sort, filter) => {
//     const queryParams = {};

//     if (params.current) queryParams.page = params.current;
//     if (params.pageSize) queryParams.limit = params.pageSize;
//     if (params.name) queryParams.search = params.name;
//     if (params.type) queryParams.type = params.type;
//     if (params.category) queryParams.category = params.category;

//     if (sort) {
//         const sortField = Object.keys(sort)[0];
//         const sortOrder = sort[sortField];
//         if (sortField && sortOrder) {
//             queryParams.sort = `${sortField}:${sortOrder === 'ascend' ? 'ASC' : 'DESC'}`;
//         }
//     }

//     try {
//         const res = await api.get('/api/items/inventory-summary', { params: queryParams });
//         return {
//             data: res.data.data,
//             total: res.data.total,
//             success: true
//         };
//     } catch (err) {
//         console.error('Error fetching inventory summary:', err);
//         return {
//             data: [],
//             total: 0,
//             success: false
//         };
//     }
// };

// ---- Items ----
export const fetchItems = () => api.get('/items').then((res) => res.data);

export const createItem = (item) => api.post('/items', item).then((res) => res.data);

export const searchItems = (type, category, query) =>
    api.get('/items/search', { params: { type, category, q: query } }).then((res) => res.data);

export const updateItem = (id, data) => api.put(`/items/${id}`, data).then((res) => res.data);

// ---- Inventory (headers + items) ----
export const addInventory = (data) => api.post('/api/inventory', data).then((res) => res.data);

export const addAsset = (data) => api.post('/api/animal-assets', data).then((res) => res.data);

export const fetchInventory = () => api.get('/api/inventory').then((res) => res.data);

export const updateInventoryHeader = (id, data) => api.put(`/inventory/${id}`, data).then((res) => res.data);

export const addPayment = (id, payment) => api.post(`/inventory/${id}/payment`, payment).then((res) => res.data);

export const fetchInventoryStats = (params) => api.get('/inventory/stats', { params }).then((res) => res.data);

export const reverseConsumption = (id) => api.post(`/consumptions/${id}/reverse`).then((res) => res.data);

// ---- Suppliers / Users ----
export const fetchSuppliers = () => api.get('/suppliers').then((res) => res.data);

export const fetchUsers = () => api.get('/users').then((res) => res.data);

export const createSupplier = (supplier) => api.post('/suppliers', supplier).then((res) => res.data);

// ---- Receipts ----
export const fetchPaymentReceipts = (params) => api.get('/payment-receipts', { params }).then((res) => res.data);

export const exportRestocks = async (format = 'csv') => {
    const response = await api.get(`/inventory/export?format=${encodeURIComponent(format)}`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `restocks.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
};

export const exportReceipts = async (params) => {
    try {
        const response = await api.get(`/payment-receipts/${params.id}/print`, {
            responseType: 'blob'
        });

        console.log('PDF response:', {
            status: response.status,
            contentType: response.headers['content-type'],
            size: response.data?.size
        });

        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/pdf'
        });

        const url = window.URL.createObjectURL(blob);

        const newWindow = window.open('', '_blank');

        if (!newWindow) {
            throw new Error('Popup blocked by browser');
        }

        newWindow.location.href = url;

        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 60000);
    } catch (error) {
        console.error('Receipt PDF error:', error);

        if (error.response) {
            console.error('Status:', error.response.status);
            console.error('Content-Type:', error.response.headers?.['content-type']);

            // Because responseType is blob, the backend error is also a Blob.
            if (error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                console.error('Server response:', text);
            }
        }

        throw error;
    }
};

export const fetchAllThresholds = () => api.get('/api/thresholds').then((res) => res.data);

export const fetchThreshold = (type) => api.get(`/api/thresholds/${type}`).then((res) => res.data);

export const updateThreshold = (type, threshold) => api.put(`/api/thresholds/${type}`, { threshold }).then((res) => res.data);

export const fetchPaymentSettings = (farmId) => api.get(`/api/farms/${farmId}/payment-settings`).then((res) => res.data);

export const savePaymentSettings = (farmId, data) => api.put(`/api/farms/${farmId}/payment-settings`, data).then((res) => res.data);

export const initiateStkPush = (data) => api.post('/api/mpesa/stk-push', data).then((res) => res.data);

export const checkStkStatus = (checkoutRequestId) => api.get(`/api/mpesa/status/${checkoutRequestId}`).then((res) => res.data);

export const fetchEtimsInvoices = (farmId) => api.get(`/api/etims/invoices/${farmId}`).then((res) => res.data);

export const submitEtimsInvoice = (data) => api.post('/api/etims/invoices', data).then((res) => res.data);

export const retryEtimsInvoice = (data) => api.post('/api/etims/invoices/retry', data).then((res) => res.data);

// api/payments.js — add this
export const fetchMpesaTransactions = (farmId, params) => api.get(`/api/mpesa/transactions/${farmId}`, { params }).then((res) => res.data);
